// Сесія кабінету в localStorage одним записом: токен, акаунт, профіль і прапорці
// пишуться і читаються атомарно, тож інша вкладка (або ця при завантаженні) не може
// побачити токен одного акаунта з профілем іншого між кількома записами.
// Модуль без залежностей: його читає store під час ініціалізації.
export const SESSION_KEY = 'auth_session';
// Окремі ключі лишаються для коду, що читає їх у своїй вкладці (main.js тощо),
// і для сесій, збережених до появи спільного запису.
export const TOKEN_KEY = 'token';
export const USER_KEY = 'keyUser';
export const INFO_KEY = 'info';
const IS_PARTNER_KEY = 'isPartner';
const IS_SHOW_BONUS_KEY = 'isShowBonus';

const readStored = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeStored = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // сховище недоступне (приватний режим тощо) — сесія живе лише у store вкладки
  }
};

const removeStored = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // ignore
  }
};

const parseJson = (raw) => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

/**
 * @param {string|null} raw
 * @returns {{token: string, keyUser: string|null, info: object, isPartner: number, isShowBonus: number}|null}
 */
export const parseSession = (raw) => {
  const session = parseJson(raw);
  if (!session || typeof session.token !== 'string' || !session.token) {
    return null;
  }

  return {
    token: session.token,
    keyUser: session.keyUser || null,
    info: session.info && typeof session.info === 'object' ? session.info : {},
    isPartner: session.isPartner ? 1 : 0,
    isShowBonus: session.isShowBonus ? 1 : 0,
  };
};

/**
 * Сесія з окремих ключів: збережена до появи спільного запису або записана вкладкою
 * зі старою версією кабінету.
 */
const readLegacySession = () => {
  const token = readStored(TOKEN_KEY);
  if (!token) {
    return null;
  }

  const info = parseJson(readStored(INFO_KEY));
  return {
    token,
    keyUser: readStored(USER_KEY),
    info: info && typeof info === 'object' ? info : {},
    isPartner: +readStored(IS_PARTNER_KEY) ? 1 : 0,
    isShowBonus: +readStored(IS_SHOW_BONUS_KEY) ? 1 : 0,
  };
};

/**
 * Сесія зі сховища. Спільний запис читається одним зверненням; окремі ключі — лише
 * коли запису немає.
 */
export const readStoredSession = () => parseSession(readStored(SESSION_KEY)) || readLegacySession();

/**
 * Вкладка зі старою версією кабінету при вході пише лише окремі ключі, а спільний запис
 * лишається зі старим токеном. Викликається на подію storage за ключем info: старий код
 * пише його останнім, тож решта ключів уже узгоджені. Якщо токен там інший, запис
 * перезбирається з окремих ключів.
 */
export const reconcileLegacySession = () => {
  const legacy = readLegacySession();
  const record = parseSession(readStored(SESSION_KEY));

  if (!legacy || !record || legacy.token === record.token) {
    return;
  }

  persistSession(legacy);
};

/**
 * Єдина точка запису сесії після входу.
 * @param {{token: string, keyUser: string, info?: object, isPartner?: number|boolean, isShowBonus?: number|boolean}} session
 */
export const persistSession = ({ token, keyUser, info = {}, isPartner = 0, isShowBonus = 0 }) => {
  const record = { token, keyUser, info, isPartner: isPartner ? 1 : 0, isShowBonus: isShowBonus ? 1 : 0 };

  writeStored(SESSION_KEY, JSON.stringify(record));
  writeStored(USER_KEY, keyUser);
  writeStored(INFO_KEY, JSON.stringify(info));
  writeStored(IS_PARTNER_KEY, record.isPartner);
  writeStored(IS_SHOW_BONUS_KEY, record.isShowBonus);
  writeStored(TOKEN_KEY, token);
};

/**
 * Оновлення профілю в записі сесії (після GET /auth/user). Профіль іншого акаунта
 * в запис не потрапляє: якщо сховище тим часом зайняв інший користувач, запис не чіпаємо.
 * @param {object} info
 */
export const updateSessionInfo = (info) => {
  const session = readStoredSession();
  if (!session || !info || session.keyUser !== 'user_' + info.id) {
    return;
  }

  persistSession({ ...session, info });
};

export const clearSession = () => {
  // Прибираємо старі ключі до атомарного запису: читач не має відновити щойно
  // видалену сесію через fallback, поки триває вихід в іншій вкладці.
  [TOKEN_KEY, USER_KEY, INFO_KEY, IS_PARTNER_KEY, IS_SHOW_BONUS_KEY, SESSION_KEY].forEach(removeStored);
};
